import type { WebLocale } from '@/enums/web-locale';

import { getUrlQueryParameters } from '@/helpers/query-parameters-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { CountryCode, getCountryCode } from '@/helpers/geo-location-helper';
import { WEB_LOCALES } from '@/constants/web-locales.constant';

interface SrDebuggerInfo {
	/** force the campaign to be part of the holdout group */
	holdoutGroup: boolean;

	/** the sponsored recommendation campaign name */
	campaignName: string;

	/** using the subdivision or geo_country to get the geo-targeted region */
	geoCountry: CountryCode | null;

	/** Do not delete the local storage on reload */
	srKeep: boolean;

	/** Allow user to see the campaign by using testing mode, without needing any other variables. */
	testingMode: boolean;

	/** Call a specific SR placement by using the a subcampaign ID. */
	subcampaignId: string;

	/** Call a specific SR ad by using the ad ID. */
	adID: string;

	/**
	 * Force the web locale as if the user selected that country.
	 * **This only works for the home page**.
	 */
	srLocale: WebLocale | null;
}

// Debugger URL keys
const USER_SR_DEBUGGER_LOCALSTORAGE_KEY = 'sr_debugger';
const FORCE_SR_QUERY = 'sr' as const;
const FORCE_HOLDOUT_QUERY = 'holdout' as const;
const FORCE_GEO_COUNTRY_QUERY = 'geo_country' as const;
const FORCE_SUBDIVISION_QUERY = 'subdivision' as const;
const FORCE_TESTING_MODE_QUERY = 'sr_testing' as const;
const FORCE_KEEP_QUERY = 'sr_keep' as const;
const FORCE_SR_SUBCAMPAIGN_QUERY = 'sr_sc' as const;
const FORCE_SR_AD_ID_QUERY = 'sr_ad_id' as const;
const FORCE_SR_LOCALE_QUERY = 'sr_locale' as const;

/**
 * Checks to see if the URL query parameter contains any Sponsored Recommendation
 * debugging information. If it does, it safely parses and returns it.
 *
 * Works in Server & Client.
 */
function parseSrDebuggerInfo() {
	const debuggingInfo: Partial<SrDebuggerInfo> = {};

	// Set the geo-country of the campaign, allowing us to return targeted campaigns without needing a VPN
	const geoCountryQueryParameter =
		getUrlQueryParameters(FORCE_SUBDIVISION_QUERY) || getUrlQueryParameters(FORCE_GEO_COUNTRY_QUERY);
	const forceGeoCountry = getCountryCode(geoCountryQueryParameter);
	if (forceGeoCountry) debuggingInfo.geoCountry = forceGeoCountry;

	// Ensure that the campaign belongs to a holdout group
	const forceHoldoutGroup = getUrlQueryParameters(FORCE_HOLDOUT_QUERY) === 'true';
	if (forceHoldoutGroup) debuggingInfo.holdoutGroup = forceHoldoutGroup;

	// Call a campaign by using it's name
	const forceCampaignName = getUrlQueryParameters(FORCE_SR_QUERY);
	if (forceCampaignName) debuggingInfo.campaignName = forceCampaignName;

	// Allow user to see the campaign by using testing mode, without needing any other variables
	const forceTestingMode = getUrlQueryParameters(FORCE_TESTING_MODE_QUERY) === 'true';
	if (forceTestingMode) debuggingInfo.testingMode = forceTestingMode;

	// Do not delete the local storage on reload
	const hasKeepOnReload = getUrlQueryParameters(FORCE_KEEP_QUERY) === 'true';
	if (hasKeepOnReload) debuggingInfo.srKeep = hasKeepOnReload;

	// Call a specific SR placement by using the a subcampaign ID
	const forceSRSubcampaignID = getUrlQueryParameters(FORCE_SR_SUBCAMPAIGN_QUERY);
	if (forceSRSubcampaignID) debuggingInfo.subcampaignId = forceSRSubcampaignID;

	// Call a specific SR ad by using the ad ID
	const forceSRAdID = getUrlQueryParameters(FORCE_SR_AD_ID_QUERY);
	if (forceSRAdID) debuggingInfo.adID = forceSRAdID;

	// Force the web locale on global routes (e.g. the home page), as if the user selected that country
	const forceSrLocale = getCountryCode(getUrlQueryParameters(FORCE_SR_LOCALE_QUERY))?.toLowerCase() ?? '';
	if (forceSrLocale in WEB_LOCALES) debuggingInfo.srLocale = forceSrLocale as WebLocale;

	return debuggingInfo;
}

/** Checks for SR debugging information and stores it into localStorage on the client. */
export function storeSrDebuggerInfo() {
	// By default, we will start things off by clearing the previous session data
	// A little way for devs to keep the previous local storage value on app init
	if (!isKeepModeEnabled()) {
		clearSrDebuggerInfomation();
	}

	const debuggingInfo = parseSrDebuggerInfo();

	// Check to ensure we have something to save into local storage
	if (import.meta.client && Object.keys(debuggingInfo).length) {
		try {
			localStorage.setItem(USER_SR_DEBUGGER_LOCALSTORAGE_KEY, JSON.stringify(debuggingInfo));
		} catch (error) {
			captureMessageForSentry(
				'[Save SR debuggers info into localStorage]:',
				{
					error: error as Error,
					where: '[useSponsoredRec.ts] storeSrDebuggerInfo()',
					localStorageKey: USER_SR_DEBUGGER_LOCALSTORAGE_KEY,
				},
				'error'
			);
		}
	}

	return debuggingInfo;
}

/**
 * Get the parsed SR Debgging values.
 *
 * *Works in Server & Client.*
 */
export function getSrDebuggerInfo(): Partial<SrDebuggerInfo> | null {
	try {
		const debuggerInputs: Partial<SrDebuggerInfo> | null = import.meta.server
			? parseSrDebuggerInfo()
			: JSON.parse(localStorage.getItem(USER_SR_DEBUGGER_LOCALSTORAGE_KEY) ?? 'null');

		if (debuggerInputs == null) return null;

		// Check if testing mode should be enabled
		if (debuggerInputs?.testingMode || Object.keys(debuggerInputs).length !== 0) debuggerInputs.testingMode = true;

		// Check geoCountry just in case local storage was manually edited
		return { ...debuggerInputs, geoCountry: getCountryCode(debuggerInputs?.geoCountry) };
	} catch (error) {
		captureMessageForSentry(
			'[Get SR debuggers info from localStorage]:',
			{
				error: error as Error,
				where: '[sponsoredrec-debugging-tools.ts] getSrDebuggerInfo()',
				localStorageKey: USER_SR_DEBUGGER_LOCALSTORAGE_KEY,
			},
			'error'
		);

		return null;
	}
}

/** Clear the stored debugger info from localStorage */
function clearSrDebuggerInfomation() {
	if (import.meta.server) return;

	localStorage.removeItem(USER_SR_DEBUGGER_LOCALSTORAGE_KEY);
}

function isKeepModeEnabled() {
	return getSrDebuggerInfo()?.srKeep === true;
}
